import type { ReactionConditions } from '@peruselab/structure-editor/chemistry/reactionParticipant';

/** The Conditions fields as typed (strings, so a half-typed number is not lost). */
export interface ConditionFields {
  readonly temperature: string;
  readonly pressure: string;
  /** In `durationUnit` (#1137 follow-up, owner: at least minutes); kept in seconds. */
  readonly duration: string;
  readonly durationUnit: DurationUnit;
  readonly yieldPercent: string;
  readonly notes: string;
}

export type DurationUnit = 'min' | 'h' | 's';

/** Seconds in one of each duration unit. */
export const SECONDS_PER: Readonly<Record<DurationUnit, number>> = { s: 1, min: 60, h: 3600 };

/** The unit a duration in seconds reads best in: hours or minutes when whole, else seconds. */
export function durationUnitFor(seconds: number): DurationUnit {
  if (seconds !== 0 && seconds % 3600 === 0) return 'h';
  if (seconds % 60 === 0) return 'min';
  return 's';
}

export const EMPTY_CONDITION_FIELDS: ConditionFields = {
  temperature: '',
  pressure: '',
  duration: '',
  durationUnit: 'min',
  yieldPercent: '',
  notes: ''
};

/**
 * The document's `conditions` from the typed fields (ADR 0008; #705): each filled, numeric field
 * is kept in the contract's fixed unit (°C, atm, s, %); empty or non-numeric fields are left out;
 * nothing filled -> undefined.
 */
export function conditionsFromFields(fields: ConditionFields): ReactionConditions | undefined {
  const number = (text: string) => (text.trim() && Number.isFinite(Number(text)) ? Number(text) : undefined);
  const temperature = number(fields.temperature);
  const pressure = number(fields.pressure);
  const duration = number(fields.duration);
  const yieldPercent = number(fields.yieldPercent);
  const result: ReactionConditions = {
    ...(temperature !== undefined ? { temperature: { value: temperature, unit: 'celsius' as const } } : {}),
    ...(pressure !== undefined ? { pressure: { value: pressure, unit: 'atm' as const } } : {}),
    ...(duration !== undefined
      ? { duration: { value: duration * SECONDS_PER[fields.durationUnit], unit: 'second' as const } }
      : {}),
    ...(yieldPercent !== undefined ? { yieldPercent } : {}),
    ...(fields.notes.trim() ? { notes: fields.notes.trim() } : {})
  };
  return Object.keys(result).length > 0 ? result : undefined;
}

/** The typed fields for a saved reaction's conditions (opening it onto the paper). */
export function fieldsFromConditions(conditions: ReactionConditions | undefined): ConditionFields {
  const text = (value: number | undefined) => (value === undefined ? '' : String(value));
  const seconds = conditions?.duration?.value;
  const durationUnit = seconds === undefined ? 'min' : durationUnitFor(seconds);
  return {
    temperature: text(conditions?.temperature?.value),
    pressure: text(conditions?.pressure?.value),
    duration: text(seconds === undefined ? undefined : seconds / SECONDS_PER[durationUnit]),
    durationUnit,
    yieldPercent: text(conditions?.yieldPercent),
    notes: conditions?.notes ?? ''
  };
}
